/**
 * useQuizLive Hook
 * Real-time hook for Kahoot-style live quiz sessions
 * Handles both team player and spectator functionality
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Alert, AppState, AppStateStatus } from "react-native";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { LOTTIE_REGISTRY, getLottieByKey, LottieItem } from "@/lib/lottie-registry";
import {
  LiveQuizSession,
  LiveQuizParticipant,
  LiveQuizAnswer,
  LiveQuizSpectator,
  Quiz,
  QuizQuestion,
  RankingEntry,
  QuestionResult,
  MascotSelection,
  calculateLiveQuizPoints,
  QuestionPhase,
  ParticipantAnswerResult,
} from "../types";

interface UseQuizLiveReturn {
  // Session state
  session: LiveQuizSession | null;
  quiz: Quiz | null;
  participants: LiveQuizParticipant[];
  spectators: LiveQuizSpectator[];
  rankings: RankingEntry[];
  
  // Current state
  currentQuestion: QuizQuestion | null;
  questionTimeRemaining: number;
  myParticipant: LiveQuizParticipant | null;
  myAnswer: LiveQuizAnswer | null;
  questionResult: QuestionResult | null;
  
  // Question phase state (countdown, active, results)
  questionPhase: QuestionPhase;
  countdownSeconds: number;
  participantResults: ParticipantAnswerResult[];
  
  // Mascot selection
  availableMascots: MascotSelection[];
  selectedMascot: MascotSelection | null;
  
  // Team role
  isTeamCaptain: boolean;
  myTeamCaptain: LiveQuizParticipant | null;
  
  // Answer reveal state
  answerRevealed: boolean;

  // Pause state
  isPaused: boolean;
  pausedForPlayer: string | null;
  pauseTimeRemaining: number; // seconds until timeout (120s)
  
  // UI state
  isLoading: boolean;
  isConnected: boolean;
  isReconnecting: boolean;
  manualRetryCount: number;
  error: string | null;
  
  // Team player actions
  joinSession: (sessionId: string) => Promise<boolean>;
  selectMascot: (mascotKey: string) => Promise<boolean>;
  setReady: () => Promise<boolean>;
  submitAnswer: (optionIndex: number) => Promise<boolean>;
  leaveSession: () => Promise<void>;
  
  // Connection recovery
  reconnect: () => Promise<void>;
  
  // Spectator actions
  joinAsSpectator: (sessionId: string) => Promise<boolean>;
  
  // Utility
  refreshSession: () => Promise<void>;
}

interface UseQuizLiveOptions {
  /** Session ID to auto-join */
  sessionId?: string;
  /** Whether user is spectating (not playing) */
  spectatorMode?: boolean;
}

/**
 * Hook for team players and spectators in live quiz
 */
export const useQuizLive = (options: UseQuizLiveOptions = {}): UseQuizLiveReturn => {
  const { sessionId: initialSessionId, spectatorMode = false } = options;
  const { activeProfile, userTeam } = useAuth();
  
  // Refs
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const subscribedSessionIdRef = useRef<string | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef<LiveQuizSession | null>(null);
  const currentQuestionRef = useRef<QuizQuestion | null>(null);
  const startQuestionTimerRef = useRef<((endsAt: string) => void) | null>(null);
  const startCountdownTimerRef = useRef<((startedAt: string) => void) | null>(null);
  
  // State
  const [session, setSession] = useState<LiveQuizSession | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [participants, setParticipants] = useState<LiveQuizParticipant[]>([]);
  const [spectators, setSpectators] = useState<LiveQuizSpectator[]>([]);
  const [myAnswer, setMyAnswer] = useState<LiveQuizAnswer | null>(null);
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(0);
  const [selectedMascotKey, setSelectedMascotKey] = useState<string | null>(null);
  
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [manualRetryCount, setManualRetryCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pauseTimeRemaining, setPauseTimeRemaining] = useState(0);
  const pauseTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Question phase state
  const [questionPhase, setQuestionPhase] = useState<QuestionPhase>("active");
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [participantResults, setParticipantResults] = useState<ParticipantAnswerResult[]>([]);
  
  // Derived state
  const myParticipant = useMemo(() => {
    if (!activeProfile?.id) return null;
    return participants.find(p => p.player_user_id === activeProfile.id) || null;
  }, [participants, activeProfile?.id]);
  
  // Ref for myParticipant to use in channel handlers (avoids dependency issues)
  const myParticipantRef = useRef<LiveQuizParticipant | null>(null);
  useEffect(() => {
    myParticipantRef.current = myParticipant;
  }, [myParticipant]);
  
  // Check if current user is team captain (the one who can answer questions)
  const isTeamCaptain = useMemo(() => {
    if (!myParticipant) return false;
    // The participant (player_user_id) is the captain for that team
    return true; // If you're a participant, you're the team's player
  }, [myParticipant]);
  
  // Find my team's captain (the participant playing for my team)
  const myTeamCaptain = useMemo(() => {
    if (!userTeam) return null;
    return participants.find(p => p.team_id === userTeam.id) || null;
  }, [participants, userTeam]);
  
  // Pause state
  const isPaused = session?.status === "paused";
  
  const pausedForPlayer = useMemo(() => {
    if (!session?.paused_for_participant_id) return null;
    const participant = participants.find(p => p.id === session.paused_for_participant_id);
    return participant?.player?.full_name || participant?.player?.nickname || "Onbekende speler";
  }, [session?.paused_for_participant_id, participants]);
  
  const currentQuestion = useMemo(() => {
    if (!session || !quiz?.questions) return null;
    const questionId = session.questions_order[session.current_question_index];
    return quiz.questions.find(q => q.id === questionId) || null;
  }, [session, quiz]);
  
  // Keep refs updated for use in callbacks
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);
  
  const rankings = useMemo((): RankingEntry[] => {
    return participants
      .filter(p => p.status !== "disconnected")
      .sort((a, b) => b.total_score - a.total_score)
      .map((participant, index) => {
        const lottie = getLottieByKey(participant.mascot_key);
        return {
          rank: index + 1,
          participant,
          total_score: participant.total_score,
          correct_answers: participant.correct_answers,
          average_time_ms: 0, // Will be calculated from answers
          // Only include mascotLottie if it has a valid source
          mascotLottie: lottie?.source ? lottie : undefined,
        };
      });
  }, [participants]);
  
  const availableMascots = useMemo((): MascotSelection[] => {
    // Filter to fun mascots (animals, monsters, fruits, emoji) with valid sources
    const mascotCategories = ["animal", "monster", "fruit", "emoji"];
    const mascots = LOTTIE_REGISTRY.filter(l => 
      mascotCategories.includes(l.category) && l.source != null
    );
    
    return mascots.map(mascot => ({
      mascot_key: mascot.key,
      mascot,
      selected_by: participants.find(p => p.mascot_key === mascot.key)?.id || null,
    }));
  }, [participants]);
  
  const selectedMascot = useMemo((): MascotSelection | null => {
    if (!selectedMascotKey) return null;
    return availableMascots.find(m => m.mascot_key === selectedMascotKey) || null;
  }, [selectedMascotKey, availableMascots]);
  
  /**
   * Fetch session data
   */
  const fetchSession = useCallback(async (sessionId: string): Promise<LiveQuizSession | null> => {
    const { data, error: fetchError } = await supabase
      .from("quiz_live_sessions")
      .select(`
        *,
        quiz:quizzes (
          *,
          sections:quiz_sections (*, questions:quiz_questions (*)),
          questions:quiz_questions (*)
        ),
        host:profiles!host_user_id (full_name, nickname)
      `)
      .eq("id", sessionId)
      .single();
    
    if (fetchError) {
      console.error("Error fetching session:", fetchError);
      return null;
    }
    
    return data as unknown as LiveQuizSession;
  }, []);
  
  /**
   * Fetch participants
   */
  const fetchParticipants = useCallback(async (sessionId: string): Promise<LiveQuizParticipant[]> => {
    const { data, error: fetchError } = await supabase
      .from("quiz_live_participants")
      .select(`
        *,
        team:teams (id, name, color, avatar_url),
        player:profiles!player_user_id (id, full_name, nickname, avatar_url)
      `)
      .eq("session_id", sessionId);
    
    if (fetchError) {
      console.error("Error fetching participants:", fetchError);
      return [];
    }
    
    return data as unknown as LiveQuizParticipant[];
  }, []);
  
  /**
   * Fetch spectators
   */
  const fetchSpectators = useCallback(async (sessionId: string): Promise<LiveQuizSpectator[]> => {
    const { data, error: fetchError } = await supabase
      .from("quiz_live_spectators")
      .select(`
        *,
        user:profiles!user_id (full_name, nickname, avatar_url)
      `)
      .eq("session_id", sessionId);
    
    if (fetchError) {
      console.error("Error fetching spectators:", fetchError);
      return [];
    }
    
    return data as unknown as LiveQuizSpectator[];
  }, []);
  
  /**
   * Subscribe to realtime updates
   */
  const subscribeToSession = useCallback((sessionId: string) => {
    // Check if already subscribed or subscribing to this exact session
    if (subscribedSessionIdRef.current === sessionId) {
      return;
    }
    
    // Mark as subscribing FIRST to prevent concurrent calls
    subscribedSessionIdRef.current = sessionId;
    
    // Clean up old channel if exists
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    // Subscribe to admin channel for broadcasts (same as where admin/edge function sends)
    const channel = supabase.channel(`quiz_live_admin:${sessionId}`);
    
    // Session updates — drive all phase transitions from DB state
    channel.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "quiz_live_sessions",
        filter: `id=eq.${sessionId}`,
      },
      (payload) => {
        setIsConnected(true);
        const newSession = payload.new as LiveQuizSession;
        const oldSession = payload.old as Partial<LiveQuizSession>;
        setSession(prev => prev ? { ...prev, ...newSession } as LiveQuizSession : null);

        if (newSession.status === "completed" || newSession.status === "cancelled") {
          setIsReconnecting(false);
          return;
        }

        // Pause: freeze the question timer at the stored remaining value
        if (newSession.status === "paused" && oldSession.status !== "paused") {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          if (newSession.time_remaining_when_paused != null) {
            setQuestionTimeRemaining(newSession.time_remaining_when_paused);
          }
        }

        // Resume: restart the question timer from the new deadline
        if (newSession.status === "playing" && oldSession.status === "paused") {
          if (newSession.question_ends_at && newSession.current_phase === "active") {
            startQuestionTimerRef.current?.(newSession.question_ends_at);
          }
        }

        // Reset per-question state when question advances
        if (newSession.current_question_index !== oldSession.current_question_index) {
          setMyAnswer(null);
          setQuestionResult(null);
          setParticipantResults([]);
          setAnswerRevealed(false);
        }

        // Drive UI phase from current_phase
        switch (newSession.current_phase) {
          case "countdown":
            if (newSession.countdown_started_at && newSession.current_phase !== oldSession.current_phase) {
              setQuestionPhase("countdown");
              setQuestionResult(null);
              setMyAnswer(null);
              setParticipantResults([]);
              setAnswerRevealed(false);
              startCountdownTimerRef.current?.(newSession.countdown_started_at);
            }
            break;
          case "active":
            if (newSession.current_phase !== oldSession.current_phase) {
              setQuestionPhase("active");
              if (newSession.question_ends_at) {
                startQuestionTimerRef.current?.(newSession.question_ends_at);
              }
            }
            break;
          case "results":
            if (newSession.current_phase !== oldSession.current_phase) {
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              setQuestionPhase("results");
              if (newSession.last_question_result) {
                setQuestionResult(newSession.last_question_result.results);
                setParticipantResults(newSession.last_question_result.participant_results ?? []);
              }
            }
            break;
        }

        // Answer revealed mid-question (showAnswer by admin)
        if (newSession.answer_revealed && !oldSession.answer_revealed) {
          setAnswerRevealed(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }
    );
    
    // Participant updates
    channel.on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "quiz_live_participants",
        filter: `session_id=eq.${sessionId}`,
      },
      async () => {
        setIsConnected(true);
        const updatedParticipants = await fetchParticipants(sessionId);
        setParticipants(updatedParticipants);
      }
    );
    
    // Answer updates (for showing who answered)
    channel.on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "quiz_live_answers",
        filter: `session_id=eq.${sessionId}`,
      },
      (payload) => {
        const answer = payload.new as LiveQuizAnswer;
        const currentParticipant = myParticipantRef.current;
        if (currentParticipant && answer.participant_id === currentParticipant.id) {
          setMyAnswer(answer);
        }
      }
    );
    
    // Subscribe to channel
    channel.subscribe((status, err) => {
      if (status === "SUBSCRIBED") {
        setIsConnected(true);
        setIsReconnecting(false);
      } else if (status === "CLOSED") {
        setIsConnected(false);
        channelRef.current = null;
        
        // Auto-reconnect after a short delay (use sessionRef for latest value)
        const currentSession = sessionRef.current;
        
        // Only reconnect if quiz is still active
        if (currentSession && (currentSession.status === "playing" || currentSession.status === "lobby")) {
          setIsReconnecting(true);
          setTimeout(() => {
            // Clear the ref so we can reconnect
            subscribedSessionIdRef.current = null;
            subscribeToSession(sessionId);
          }, 1000);
        } else {
          // Quiz ended or cancelled - don't try to reconnect
          setIsReconnecting(false);
        }
      } else if (status === "CHANNEL_ERROR") {
        console.error("[QuizLive] ⚠️ Channel error:", err);
        setIsConnected(false);
      }
    });
    
    channelRef.current = channel;
  }, [fetchParticipants]);
  
  /**
   * Start pause countdown timer (2 minutes)
   */
  const startPauseTimer = useCallback((pausedAt: string) => {
    if (pauseTimerRef.current) {
      clearInterval(pauseTimerRef.current);
    }
    
    const pauseTime = new Date(pausedAt).getTime();
    const PAUSE_TIMEOUT_MS = 120000; // 2 minutes
    
    const updateTimer = () => {
      const elapsed = Date.now() - pauseTime;
      const remaining = Math.max(0, Math.ceil((PAUSE_TIMEOUT_MS - elapsed) / 1000));
      setPauseTimeRemaining(remaining);
      
      if (remaining <= 0 && pauseTimerRef.current) {
        clearInterval(pauseTimerRef.current);
      }
    };
    
    updateTimer();
    pauseTimerRef.current = setInterval(updateTimer, 1000);
  }, []);
  
  /**
   * Start question timer countdown
   */
  const startQuestionTimer = useCallback((endsAt: string) => {
    if (timerRef.current) clearInterval(timerRef.current);

    const endTime = new Date(endsAt).getTime();

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setQuestionTimeRemaining(remaining);

      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 100);
  }, []);
  
  // Keep ref updated (to avoid circular dependency in callbacks)
  useEffect(() => {
    startQuestionTimerRef.current = startQuestionTimer;
  }, [startQuestionTimer]);

  /**
   * Run 5-4-3-2-1 countdown locally from a server-set start timestamp.
   * Admin sets countdown_started_at slightly in the future to absorb network
   * latency; clamping elapsed to 0 ensures we always start the visible countdown
   * at the full COUNTDOWN_SECONDS even if we receive the event very quickly.
   */
  const startCountdownTimer = useCallback((countdownStartedAt: string) => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    const startTime = new Date(countdownStartedAt).getTime();
    const COUNTDOWN_SECONDS = 5;

    const updateTimer = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
      const remaining = Math.max(0, COUNTDOWN_SECONDS - elapsed);
      setCountdownSeconds(remaining);

      if (remaining <= 0 && countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };

    updateTimer();
    countdownTimerRef.current = setInterval(updateTimer, 100);
  }, []);

  useEffect(() => {
    startCountdownTimerRef.current = startCountdownTimer;
  }, [startCountdownTimer]);
  
  /**
   * Join session as team player (all team members can join to watch, only captain can answer)
   */
  const joinSession = useCallback(async (sessionId: string): Promise<boolean> => {
    // Early return if already joined this session
    if (subscribedSessionIdRef.current === sessionId && session?.id === sessionId) {
      return true;
    }
    
    if (!activeProfile?.id || !userTeam) {
      setError("Je moet in een team zitten om mee te doen");
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if user is in the team (any member can join, but only captain can answer)
      const { data: memberData, error: memberError } = await supabase
        .from("team_members")
        .select("is_captain")
        .eq("team_id", userTeam.id)
        .eq("user_id", activeProfile.id)
        .single();
      
      if (memberError) {
        setError("Je zit niet in een team");
        return false;
      }
      
      const isCaptain = memberData?.is_captain || false;
      
      // Fetch session
      const sessionData = await fetchSession(sessionId);
      if (!sessionData) {
        setError("Quiz sessie niet gevonden");
        return false;
      }
      
      // Allow joining in lobby or if quiz is in progress (for reconnection)
      if (sessionData.status !== "lobby" && sessionData.status !== "playing" && sessionData.status !== "paused") {
        setError("De quiz is al afgelopen");
        return false;
      }
      
      setSession(sessionData);
      setQuiz(sessionData.quiz || null);
      
      // Check existing participants
      const existingParticipants = await fetchParticipants(sessionId);
      const teamParticipant = existingParticipants.find(p => p.team_id === userTeam.id);
      
      // If team already has a participant (captain)
      if (teamParticipant) {
        // If I'm the captain and I'm reconnecting
        if (teamParticipant.player_user_id === activeProfile.id) {
          setSelectedMascotKey(teamParticipant.mascot_key);
          
          // On reconnect: always clear "disconnected", preserve "ready" only in lobby
          const newStatus = teamParticipant.is_ready && sessionData.status === "lobby"
            ? "ready"
            : (sessionData.status === "lobby" ? "waiting" : "playing");
          
          await supabase
            .from("quiz_live_participants")
            .update({ 
              status: newStatus,
              last_seen_at: new Date().toISOString() 
            })
            .eq("id", teamParticipant.id);
        }
        // Otherwise I'm a team member watching (not the captain)
      } else if (sessionData.status === "lobby" && isCaptain) {
        // Only captain can register the team in lobby phase
        // Team member will join via the existing participant entry
      } else if (sessionData.status === "lobby" && !isCaptain) {
        // Non-captains need to wait for captain to join first
        setError("De teamkapitein moet eerst deelnemen aan de quiz");
        return false;
      }
      
      setParticipants(existingParticipants);
      
      // Subscribe to updates
      subscribeToSession(sessionId);
      
      return true;
    } catch (err) {
      console.error("Error joining session:", err);
      setError("Fout bij deelnemen aan quiz");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [activeProfile?.id, userTeam, fetchSession, fetchParticipants, subscribeToSession]);
  
  /**
   * Select mascot (creates participant if not exists)
   */
  const selectMascot = useCallback(async (mascotKey: string): Promise<boolean> => {
    if (!session?.id || !activeProfile?.id || !userTeam) {
      return false;
    }
    
    // Check if mascot is already taken
    const mascotTaken = participants.find(
      p => p.mascot_key === mascotKey && p.player_user_id !== activeProfile.id
    );
    
    if (mascotTaken) {
      Alert.alert("Al gekozen", "Deze mascotte is al gekozen door een ander team");
      return false;
    }
    
    setIsLoading(true);
    
    try {
      if (myParticipant) {
        // Update existing participant
        const { error: updateError } = await supabase
          .from("quiz_live_participants")
          .update({ mascot_key: mascotKey })
          .eq("id", myParticipant.id);
        
        if (updateError) throw updateError;
      } else {
        // Create new participant
        const { error: insertError } = await supabase
          .from("quiz_live_participants")
          .insert({
            session_id: session.id,
            team_id: userTeam.id,
            player_user_id: activeProfile.id,
            mascot_key: mascotKey,
            status: "waiting",
            is_ready: false,
          });
        
        if (insertError) {
          if (insertError.code === "23505") {
            // Unique constraint violation - mascot already taken
            Alert.alert("Al gekozen", "Deze mascotte is net gekozen door iemand anders");
            return false;
          }
          throw insertError;
        }
      }
      
      setSelectedMascotKey(mascotKey);
      
      // Refetch participants
      const updatedParticipants = await fetchParticipants(session.id);
      setParticipants(updatedParticipants);
      
      return true;
    } catch (err) {
      console.error("Error selecting mascot:", err);
      setError("Fout bij kiezen mascotte");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [session?.id, activeProfile?.id, userTeam, participants, myParticipant, fetchParticipants]);
  
  /**
   * Set ready to play
   */
  const setReady = useCallback(async (): Promise<boolean> => {
    if (!myParticipant) {
      setError("Je moet eerst een mascotte kiezen");
      return false;
    }
    
    try {
      const { error: updateError } = await supabase
        .from("quiz_live_participants")
        .update({ is_ready: true, status: "ready" })
        .eq("id", myParticipant.id);
      
      if (updateError) throw updateError;
      
      return true;
    } catch (err) {
      console.error("Error setting ready:", err);
      return false;
    }
  }, [myParticipant]);
  
  /**
   * Submit answer
   */
  const submitAnswer = useCallback(async (optionIndex: number): Promise<boolean> => {
    if (!session?.id || !myParticipant || !currentQuestion) {
      return false;
    }
    
    // Check if already answered
    if (myAnswer) {
      return false;
    }
    
    try {
      // Calculate time taken
      const questionStartTime = session.question_started_at 
        ? new Date(session.question_started_at).getTime()
        : Date.now();
      const timeTakenMs = Date.now() - questionStartTime;
      
      // Check if answer is correct
      const isCorrect = currentQuestion.options[optionIndex]?.is_correct || false;
      
      // Calculate points
      const timerMs = (session.timer_seconds || 30) * 1000;
      const { points, bonus } = calculateLiveQuizPoints(isCorrect, timeTakenMs, timerMs);
      
      // Submit answer
      const { data, error: submitError } = await supabase
        .from("quiz_live_answers")
        .insert({
          session_id: session.id,
          participant_id: myParticipant.id,
          question_id: currentQuestion.id,
          selected_option_index: optionIndex,
          is_correct: isCorrect,
          time_taken_ms: timeTakenMs,
          points_earned: points,
          bonus_earned: bonus,
        })
        .select()
        .single();
      
      if (submitError) {
        if (submitError.code === "23505") {
          // Already answered
          return false;
        }
        throw submitError;
      }
      
      setMyAnswer(data as LiveQuizAnswer);
      
      return true;
    } catch (err) {
      console.error("Error submitting answer:", err);
      return false;
    }
  }, [session, myParticipant, currentQuestion, myAnswer]);
  
  /**
   * Leave session
   */
  const leaveSession = useCallback(async () => {
    
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    subscribedSessionIdRef.current = null;
    
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Update status to disconnected
    if (myParticipant) {
      await supabase
        .from("quiz_live_participants")
        .update({ status: "disconnected" })
        .eq("id", myParticipant.id);
    }
    
    // Reset state
    setSession(null);
    setQuiz(null);
    setParticipants([]);
    setSpectators([]);
    setMyAnswer(null);
    setQuestionResult(null);
    setIsConnected(false);
    setSelectedMascotKey(null);
  }, [myParticipant]);
  
  /**
   * Join as spectator
   */
  const joinAsSpectator = useCallback(async (sessionId: string): Promise<boolean> => {
    if (!activeProfile?.id) {
      return false;
    }
    
    setIsLoading(true);
    
    try {
      // Fetch session
      const sessionData = await fetchSession(sessionId);
      if (!sessionData) {
        setError("Quiz sessie niet gevonden");
        return false;
      }
      
      setSession(sessionData);
      setQuiz(sessionData.quiz || null);
      
      // Create spectator entry
      await supabase
        .from("quiz_live_spectators")
        .upsert({
          session_id: sessionId,
          user_id: activeProfile.id,
          team_id: userTeam?.id || null,
        });
      
      // Fetch participants and spectators
      const [participantsData, spectatorsData] = await Promise.all([
        fetchParticipants(sessionId),
        fetchSpectators(sessionId),
      ]);
      
      setParticipants(participantsData);
      setSpectators(spectatorsData);
      
      // Subscribe to updates
      subscribeToSession(sessionId);
      
      return true;
    } catch (err) {
      console.error("Error joining as spectator:", err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [activeProfile?.id, userTeam?.id, fetchSession, fetchParticipants, fetchSpectators, subscribeToSession]);
  
  /**
   * Refresh session data
   */
  const refreshSession = useCallback(async () => {
    if (!session?.id) return;
    
    const [sessionData, participantsData] = await Promise.all([
      fetchSession(session.id),
      fetchParticipants(session.id),
    ]);
    
    if (sessionData) {
      setSession(sessionData);
      setQuiz(sessionData.quiz || null);
    }
    setParticipants(participantsData);
  }, [session?.id, fetchSession, fetchParticipants]);
  
  // Auto-join if sessionId provided
  useEffect(() => {
    if (initialSessionId && activeProfile?.id) {
      if (spectatorMode) {
        joinAsSpectator(initialSessionId);
      } else {
        joinSession(initialSessionId);
      }
    }
  }, [initialSessionId, activeProfile?.id, spectatorMode]);
  
  // On initial join (or reconnect), restore timers and phase from DB state
  useEffect(() => {
    if (!session?.current_phase) return;

    if (session.current_phase === "countdown" && session.countdown_started_at) {
      setQuestionPhase("countdown");
      startCountdownTimer(session.countdown_started_at);
    } else if (session.current_phase === "active" && session.question_ends_at) {
      setQuestionPhase("active");
      startQuestionTimer(session.question_ends_at);
    } else if (session.current_phase === "results" && session.last_question_result) {
      setQuestionPhase("results");
      setQuestionResult(session.last_question_result.results);
      setParticipantResults(session.last_question_result.participant_results ?? []);
    }

    if (session.answer_revealed) setAnswerRevealed(true);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  // Only run when we first get session data (join/reconnect), not on every update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);
  
  // Heartbeat to keep connection alive and update last_seen (2s interval)
  useEffect(() => {
    if (!myParticipant?.id) return;

    const sendHeartbeat = async () => {
      await supabase
        .from("quiz_live_participants")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("id", myParticipant.id);
    };

    // Send immediately on mount / reconnect
    sendHeartbeat();
    heartbeatRef.current = setInterval(sendHeartbeat, 2000);
    
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [myParticipant?.id]);
  
  // Start pause timer when session becomes paused
  useEffect(() => {
    if (session?.status === "paused" && session.paused_at) {
      startPauseTimer(session.paused_at);
    } else if (session?.status !== "paused" && pauseTimerRef.current) {
      clearInterval(pauseTimerRef.current);
      setPauseTimeRemaining(0);
    }
  }, [session?.status, session?.paused_at, startPauseTimer]);
  
  /**
   * Manual reconnect — exposed for ConnectionLostModal "Opnieuw verbinden" button.
   * Tears down the channel, forces a websocket reconnect, re-subscribes, and
   * re-syncs all state from the DB.
   */
  const reconnect = useCallback(async () => {
    const sid = session?.id || sessionRef.current?.id;
    if (!sid) return;

    setIsReconnecting(true);
    setManualRetryCount(prev => prev + 1);

    // 1. Tear down old channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // 2. Force websocket back up
    try {
      supabase.realtime.connect();
    } catch {
      // connect() may throw if already connected — safe to ignore
    }

    // 3. Re-subscribe
    subscribedSessionIdRef.current = null;
    subscribeToSession(sid);

    // 4. Re-sync state from DB
    const [freshSession, freshParticipants] = await Promise.all([
      fetchSession(sid),
      fetchParticipants(sid),
    ]);
    if (freshSession) {
      setSession(freshSession);
      setQuiz(freshSession.quiz || null);
    }
    setParticipants(freshParticipants);

    // 5. Fresh heartbeat so the watcher sees us immediately
    const participant = myParticipantRef.current;
    if (participant?.id) {
      await supabase
        .from("quiz_live_participants")
        .update({
          last_seen_at: new Date().toISOString(),
          status: freshSession?.status === "lobby" ? "waiting" : "playing",
        })
        .eq("id", participant.id);
    }
  }, [session?.id, subscribeToSession, fetchSession, fetchParticipants]);

  // Reset manual retry count when connection is restored
  useEffect(() => {
    if (isConnected) setManualRetryCount(0);
  }, [isConnected]);

  // AppState listener — send final heartbeat on background, resync on foreground
  useEffect(() => {
    const handleAppState = (nextState: AppStateStatus) => {
      const participant = myParticipantRef.current;
      const sid = sessionRef.current?.id;
      if (!participant?.id || !sid) return;

      if (nextState === "background" || nextState === "inactive") {
        // Last heartbeat before going to sleep
        supabase
          .from("quiz_live_participants")
          .update({ last_seen_at: new Date().toISOString() })
          .eq("id", participant.id);
      } else if (nextState === "active") {
        // Came back — immediate heartbeat + force re-subscribe
        supabase
          .from("quiz_live_participants")
          .update({ last_seen_at: new Date().toISOString() })
          .eq("id", participant.id);

        subscribedSessionIdRef.current = null;
        subscribeToSession(sid);
      }
    };

    const sub = AppState.addEventListener("change", handleAppState);
    return () => sub.remove();
  }, [subscribeToSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      subscribedSessionIdRef.current = null;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (pauseTimerRef.current) clearInterval(pauseTimerRef.current);
    };
  }, []);
  
  return {
    // Session state
    session,
    quiz,
    participants,
    spectators,
    rankings,
    
    // Current state
    currentQuestion,
    questionTimeRemaining,
    myParticipant,
    myAnswer,
    questionResult,
    
    // Question phase state
    questionPhase,
    countdownSeconds,
    participantResults,
    
    // Mascot selection
    availableMascots,
    selectedMascot,
    
    // Team role
    isTeamCaptain,
    myTeamCaptain,

    // Answer reveal state
    answerRevealed,

    // Pause state
    isPaused,
    pausedForPlayer,
    pauseTimeRemaining,
    
    // UI state
    isLoading,
    isConnected,
    isReconnecting,
    manualRetryCount,
    error,
    
    // Actions
    joinSession,
    selectMascot,
    setReady,
    submitAnswer,
    leaveSession,
    reconnect,
    joinAsSpectator,
    refreshSession,
  };
};

